import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, XIcon, FlameIcon, MoonIcon, LayersIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon, ClockIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { usePlatform } from '../context/PlatformContext';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useStoryStack } from '../hooks/useStoryStack';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import BackButton from '../components/UI/BackButton';
import { tropes, relationshipTypes, genres, spiceLevels, darknessLevels, endingTypes, povTypes, tenseTypes } from '../data/bookTags';
const QUICK_MOODS = ['Dark Romance', 'Hockey Romance', 'Why Choose', 'Enemies to Lovers', 'Small Town Romance', 'Morally Grey Hero', 'Slow Burn', 'Paranormal Romance'];
const TRENDING_STACKS = [{
  name: 'Dark Mafia Enemies to Lovers',
  tags: ['Dark Romance', 'Enemies to Lovers', 'Mafia']
}, {
  name: 'Why Choose Hockey Romance',
  tags: ['Why Choose', 'Hockey', 'Romance']
}, {
  name: 'Grumpy Sunshine Small Town',
  tags: ['Grumpy Sunshine', 'Small Town', 'Contemporary Romance']
}, {
  name: 'Paranormal Fated Mates Slow Burn',
  tags: ['Paranormal', 'Fated Mates', 'Slow Burn']
}];
// Filter categories for the compact bar
const FILTER_CATEGORIES = [{
  key: 'tropes',
  label: 'Tropes',
  items: tropes,
  icon: null
}, {
  key: 'relationship',
  label: 'Dynamics',
  items: relationshipTypes,
  icon: null
}, {
  key: 'genres',
  label: 'Genres',
  items: genres,
  icon: null
}, {
  key: 'pov',
  label: 'POV',
  items: povTypes,
  icon: <EyeIcon className="h-3 w-3 text-indigo-400" data-id="element-14144" data-mp="3kh2i3ffo2yet" />
}, {
  key: 'tense',
  label: 'Tense',
  items: tenseTypes,
  icon: <ClockIcon className="h-3 w-3 text-emerald-400" data-id="element-14145" data-mp="2hzv429ndvzq" />
}, {
  key: 'spice',
  label: 'Spice',
  items: spiceLevels,
  icon: <FlameIcon className="h-3 w-3 text-orange-500" data-id="element-14146" data-mp="79qcutawzm16" />
}, {
  key: 'darkness',
  label: 'Darkness',
  items: darknessLevels,
  icon: <MoonIcon className="h-3 w-3 text-slate-400" data-id="element-14147" data-mp="3pkl777u2md5i" />
}, {
  key: 'ending',
  label: 'Ending',
  items: endingTypes,
  icon: null
}];
// Desktop collapsible filter section
const FilterSection = ({
  title,
  items,
  selectedTags,
  onToggle,
  renderItem
}: {
  title: string;
  items: any[];
  selectedTags: string[];
  onToggle: (tag: string) => void;
  renderItem: (item: any, isSelected: boolean, onClick: () => void) => React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dk = useDarkClasses();
  return <div className={`border-b ${dk.border} py-3 sm:py-4`} data-id="element-14148" data-mp="2wwz749k1xqne">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between w-full text-left focus:outline-none" data-id="element-14149" data-mp="31x3ixma0lx7a">
        <span className={`font-bold text-sm sm:text-base ${dk.textPrimary}`} data-id="element-14150" data-mp="2uc14ujaghocv">
          {title}
        </span>
        {isOpen ? <ChevronUpIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${dk.textMuted}`} data-id="element-14151" data-mp="33eadt4q2t9o5" /> : <ChevronDownIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${dk.textMuted}`} data-id="element-14152" data-mp="33bwj4wzb5zh4" />}
      </button>
      <AnimatePresence data-id="element-14153" data-mp="2ap3g27yg4o3y">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-14154" data-mp="2apv0qnqi91fg">
            <div className="pt-3 sm:pt-4 flex flex-wrap gap-1.5 sm:gap-2" data-id="element-14155" data-mp="1ryu62bwf3s1p">
              {items.map((item: any) => renderItem(item, selectedTags.includes(item.name || item), () => onToggle(item.name || item)))}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
// Compact mobile filter bar with search + category chips
const MobileFilterBar = ({
  selectedTags,
  onToggle,
  accentBg,
  accentBtnText,
  accentBorder,
  accentHoverBorder,
  accentHoverText,
  accentText
}: {
  selectedTags: string[];
  onToggle: (tag: string) => void;
  accentBg: string;
  accentBtnText: string;
  accentBorder: string;
  accentHoverBorder: string;
  accentHoverText: string;
  accentText: string;
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const toggleCategory = (key: string) => {
    setActiveCategory(activeCategory === key ? null : key);
    setFilterSearch('');
  };
  const activeItems = FILTER_CATEGORIES.find(c => c.key === activeCategory)?.items || [];
  const filteredItems = filterSearch ? activeItems.filter((item: any) => (item.name || '').toLowerCase().includes(filterSearch.toLowerCase())) : activeItems;
  // Count selected in each category
  const getSelectedCount = (items: any[]) => items.filter((item: any) => selectedTags.includes(item.name)).length;
  return <div ref={dropdownRef} className="relative" data-id="element-14156" data-mp="2fphot04l5i9d">
      {/* Compact bar: search + category chips */}
      <div className={`flex items-center gap-2 px-4 py-3 border-b ${dk.border} ${dk.surface}`} data-id="element-14157" data-mp="2aqxy6mf1riao">
        {/* Mini search */}
        <div className="relative flex-shrink-0 w-8" data-id="element-14158" data-mp="2c95156gx1vsh">
          <FilterIcon className={`h-4 w-4 ${accentText}`} data-id="element-14159" data-mp="2h6ofe6dm68kf" />
        </div>

        {/* Scrollable category chips */}
        <div className="flex-1 flex items-center overflow-x-auto gap-2 scrollbar-hide" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-14160" data-mp="237oth6hhk7ry">
          {FILTER_CATEGORIES.map(cat => {
          const count = getSelectedCount(cat.items);
          const isActive = activeCategory === cat.key;
          return <button key={cat.key} onClick={() => toggleCategory(cat.key)} className={`flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 ${rt.badgeRadius} text-xs font-medium border transition-all ${isActive ? `${accentBg} ${accentBtnText} ${accentBorder}` : count > 0 ? `${accentBg}/20 ${accentText} ${accentBorder}` : `${dk.surfaceMuted} ${dk.textSecondary} border-transparent`}`} data-id="element-14161" data-mp="1kh7d81dub4nh">
                {cat.icon}
                {cat.label}
                {count > 0 && <span className={`ml-0.5 px-1.5 py-0.5 ${rt.badgeRadius} text-[10px] font-bold ${isActive ? `${dk.surfaceMuted} ${accentText}` : `${accentBg} ${accentBtnText}`}`} data-id="element-14162" data-mp="qy972cxdbbun">
                    {count}
                  </span>}
                <ChevronDownIcon className={`h-3 w-3 transition-transform ${isActive ? 'rotate-180' : ''}`} data-id="element-14163" data-mp="1etc4vae5gpn9" />
              </button>;
        })}
        </div>
      </div>

      {/* Dropdown panel for active category */}
      <AnimatePresence data-id="element-14164" data-mp="1jkty1c9xllxo">
        {activeCategory && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className={`overflow-hidden border-b ${dk.border} ${dk.surface}`} data-id="element-14165" data-mp="1jngg45ug7ju5">
            <div className="px-4 py-3" data-id="element-14166" data-mp="34w0wp1ft7345">
              {/* Search within category */}
              <div className="relative mb-3" data-id="element-14167" data-mp="39xzzoa7mzy0k">
                <SearchIcon className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${dk.textMuted}`} data-id="element-14168" data-mp="39uu6ojvw0bzk" />
                <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder={`Search ${FILTER_CATEGORIES.find(c => c.key === activeCategory)?.label || ''}...`} className={`w-full pl-8 pr-3 py-2 rounded-lg text-xs border focus:outline-none focus:border-[${accentBg === 'bg-[#e2b04a]' ? '#e2b04a' : '#7E8CE0'}] ${dk.input}`} autoFocus data-id="element-14169" data-mp="2h92lai6jqld3" />
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-id="element-14170" data-mp="1uzly4miz9rto">
                {filteredItems.map((item: any) => {
              const isSelected = selectedTags.includes(item.name);
              const cat = FILTER_CATEGORIES.find(c => c.key === activeCategory);
              return <button key={item.id || item.name} onClick={() => onToggle(item.name)} className={`inline-flex items-center px-2.5 py-1 ${rt.badgeRadius} text-xs font-medium border transition-all ${isSelected ? `${accentBg} ${accentBtnText} ${accentBorder} shadow-sm` : `${dk.surfaceMuted} ${dk.textSecondary} border-transparent ${accentHoverBorder} ${accentHoverText}`}`} data-id="element-14171" data-mp="1gzb63g5xm76x">
                      {cat?.icon && <span className="mr-1" data-id="element-14172" data-mp="t974tfg7ujj3">{cat.icon}</span>}
                      {item.name}
                    </button>;
            })}
                {filteredItems.length === 0 && <span className={`text-xs italic ${dk.textMuted} py-2`} data-id="element-14173" data-mp="33ifx8oi5w86d">
                    No matches
                  </span>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const StoryStackDiscovery = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const {
    selectedTags,
    moodInput,
    setMoodInput,
    addTag,
    removeTag,
    clearAll,
    filteredBooks,
    setSelectedTags
  } = useStoryStack();
  // AfterTide accent colors
  const accentBg = isAfterTide ? 'bg-[#e2b04a]' : 'bg-[#7E8CE0]';
  const accentBgHover = isAfterTide ? 'hover:bg-[#d1a03a]' : 'hover:bg-[#6A78D1]';
  const accentBorder = isAfterTide ? 'border-[#e2b04a]' : 'border-[#7E8CE0]';
  const accentText = isAfterTide ? 'text-[#e2b04a]' : 'text-[#7E8CE0]';
  const accentFocusBorder = isAfterTide ? 'focus:border-[#e2b04a]' : 'focus:border-[#7E8CE0]';
  const accentHoverBorder = isAfterTide ? 'hover:border-[#e2b04a]' : 'hover:border-[#7E8CE0]';
  const accentHoverText = isAfterTide ? 'hover:text-[#e2b04a]' : 'hover:text-[#7E8CE0]';
  const accentBtnText = isAfterTide ? 'text-[#1a1a2e]' : 'text-white';
  const [searchParams] = useSearchParams();
  const fromGalaxy = searchParams.get('from') === 'galaxy';
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (moodInput.trim()) {
      addTag(moodInput.trim());
      setMoodInput('');
    }
  };
  const handleToggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      removeTag(tag);
    } else {
      addTag(tag);
    }
  };
  const renderPill = (label: string, isSelected: boolean, onClick: () => void, icon?: React.ReactNode) => <button key={label} onClick={onClick} className={`inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 ${rt.badgeRadius} text-xs sm:text-sm font-medium border transition-all ${isSelected ? `${accentBg} ${accentBtnText} ${accentBorder} shadow-sm` : `${dk.surfaceMuted} ${dk.textSecondary} border-transparent ${accentHoverBorder} ${accentHoverText}`}`} data-id="element-14174" data-mp="2pdgqq4eizr1y">
      {icon && <span className="mr-1 sm:mr-1.5" data-id="element-14175" data-mp="2u1q98w3gsbck">{icon}</span>}
      {label}
    </button>;
  // Desktop filter sidebar content
  const filterContent = <>
      <FilterSection title="Tropes" items={tropes} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick)} data-id="element-14176" data-mp="2l23542jcorft" />
      <FilterSection title="Relationship Dynamics" items={relationshipTypes} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick)} data-id="element-14177" data-mp="2bcryqnnum0vm" />
      <FilterSection title="Genres" items={genres} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick)} data-id="element-14178" data-mp="1xdjzxvvlkury" />
      <FilterSection title="POV" items={povTypes} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick, <EyeIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-indigo-400" data-id="element-14180" data-mp="3pa0hplywy8u8" />)} data-id="element-14179" data-mp="lv4sc97kl1qt" />
      <FilterSection title="Tense" items={tenseTypes} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick, <ClockIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-400" data-id="element-14182" data-mp="36j9cmwlyl4ln" />)} data-id="element-14181" data-mp="3u8libmi2uuj7" />
      <FilterSection title="Spice Level" items={spiceLevels} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick, <FlameIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-orange-500" data-id="element-14184" data-mp="1qw4fs3xs2v8k" />)} data-id="element-14183" data-mp="3bi3tht0d8j34" />
      <FilterSection title="Darkness Level" items={darknessLevels} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick, <MoonIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400" data-id="element-14186" data-mp="29qjdzrc9mjvc" />)} data-id="element-14185" data-mp="1m1i2fv84yslu" />
      <FilterSection title="Ending Type" items={endingTypes} selectedTags={selectedTags} onToggle={handleToggleTag} renderItem={(item, isSelected, onClick) => renderPill(item.name, isSelected, onClick)} data-id="element-14187" data-mp="24rzoxi2dkryr" />
    </>;
  return <div className={`min-h-screen ${dk.page}`} data-id="element-14188" data-mp="2m5hfx3ydiz77">
      {fromGalaxy && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2" data-id="element-14189" data-mp="2r1q394mq5ypg">
          <BackButton to="/story-galaxy" label="Back to Story Galaxy" data-id="element-14190" data-mp="2d9s0dbjlwz1q" />
        </div>}

      {/* Section 1: Hero Mood Entry */}
      <section className={`${fromGalaxy ? 'pt-4' : 'pt-8 sm:pt-12'} pb-8 sm:pb-10 px-4 sm:px-6 lg:px-8 ${dk.surface} border-b ${dk.border}`} data-id="element-14191" data-mp="2halq8agm0nx9">
        <div className="max-w-4xl mx-auto text-center" data-id="element-14192" data-mp="28c06ibzej2uw">
          <h1 className={`text-2xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${dk.textHeading}`} data-id="element-14193" data-mp="289dr5q69rj24">
            {isAfterTide ? 'What dark story calls to you tonight?' : 'What kind of story are you in the mood for today?'}
          </h1>
          <p className={`text-base sm:text-lg mb-6 sm:mb-8 ${dk.textSecondary}`} data-id="element-14194" data-mp="3onvpo4f91z0g">
            {isAfterTide ? 'Describe the intensity you crave, or choose from popular moods to build your Story Stack.' : 'Type in the kind of story you want, or choose from popular story moods to build your Story Stack.'}
          </p>

          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-6 sm:mb-8" data-id="element-14195" data-mp="wj91mukz1ehb">
            <div className="flex gap-2 sm:gap-0 sm:relative" data-id="element-14196" data-mp="rl7lahexjt9j">
              <div className="relative flex-1" data-id="element-14197" data-mp="1o8ki0015pq43">
                <SearchIcon className={`absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 ${dk.textMuted}`} data-id="element-14198" data-mp="1obghvtwkeov6" />
                <input type="text" value={moodInput} onChange={e => setMoodInput(e.target.value)} placeholder={isAfterTide ? 'Try: dark mafia enemies-to-lovers' : 'Try: dark enemies-to-lovers mafia romance'} className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-28 py-3 sm:py-4 rounded-xl text-base sm:text-lg border-2 focus:outline-none ${accentFocusBorder} transition-colors ${dk.input} shadow-sm`} data-id="element-14199" data-mp="1ffr0rx25ggnl" />
                <button type="submit" className={`hidden sm:block absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 ${accentBg} ${accentBgHover} ${accentBtnText} rounded-lg font-bold transition-colors`} data-id="element-14200" data-mp="2kf5o7mn9xz03">
                  Add
                </button>
              </div>
              <button type="submit" className={`sm:hidden flex-shrink-0 px-4 py-3 ${accentBg} ${accentBgHover} ${accentBtnText} rounded-xl font-bold transition-colors`} data-id="element-14201" data-mp="2ye5fz3632u0h">
                Add
              </button>
            </div>
          </form>

          <div className="flex overflow-x-auto pb-2 scrollbar-hide gap-2 sm:gap-3 justify-start sm:justify-center sm:flex-wrap px-1 sm:px-0" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-14202" data-mp="3c8zci8e0nw6u">
            {QUICK_MOODS.map(mood => renderPill(mood, selectedTags.includes(mood), () => handleToggleTag(mood)))}
          </div>
        </div>
      </section>

      {/* Section 2: Sticky Story Stack Builder */}
      <div className={`sticky top-0 z-30 ${dk.surface} border-b ${dk.border} shadow-sm`} style={{
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)'
    }} data-id="element-14203" data-mp="3jqf1swya8o69">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4" data-id="element-14204" data-mp="3tht7jmgtuk7u">
          <div className="flex items-center justify-between gap-3 sm:gap-4" data-id="element-14205" data-mp="3ophwbngmzbzz">
            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-14206" data-mp="310guwk681147">
              <LayersIcon className={`h-5 w-5 sm:h-6 sm:w-6 ${accentText}`} data-id="element-14207" data-mp="30ym8f1fax0y0" />
              <h2 className={`text-sm sm:text-lg font-bold ${dk.textHeading} whitespace-nowrap`} data-id="element-14208" data-mp="2w0avuixmgcc3">
                Stack{' '}
                <span className={accentText} data-id="element-14209" data-mp="1ga1okb5duazw">({selectedTags.length})</span>
              </h2>
            </div>

            <div className="flex-1 flex items-center overflow-x-auto scrollbar-hide gap-1.5 sm:gap-2 min-h-[36px] sm:min-h-[40px]" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-14210" data-mp="11lf6w2sa975i">
              <AnimatePresence mode="popLayout" data-id="element-14211" data-mp="1z8w3bk4ujbg2">
                {selectedTags.length === 0 ? <motion.span key="empty" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className={`text-xs sm:text-sm italic ${dk.textMuted} whitespace-nowrap`} data-id="element-14212" data-mp="1l94v6010gp9z">
                    Start building your story stack...
                  </motion.span> : selectedTags.map(tag => <motion.span key={tag} initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0.8,
                opacity: 0
              }} layout className={`inline-flex items-center flex-shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 ${rt.badgeRadius} ${accentBg} ${accentBtnText} text-xs sm:text-sm font-medium shadow-sm whitespace-nowrap`} data-id="element-14213" data-mp="3g9hj01xwgbep">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="ml-1.5 sm:ml-2 opacity-70 hover:opacity-100 focus:outline-none" data-id="element-14214" data-mp="2emqa94iluf6p">
                        <XIcon className="h-3 w-3 sm:h-4 sm:w-4" data-id="element-14215" data-mp="1r04of7pfrf8f" />
                      </button>
                    </motion.span>)}
              </AnimatePresence>
            </div>

            {selectedTags.length > 0 && <button onClick={clearAll} className={`flex-shrink-0 text-xs sm:text-sm font-medium ${dk.textMuted} hover:text-red-500 transition-colors whitespace-nowrap`} data-id="element-14216" data-mp="2dzizji1nff9o">
                Clear
              </button>}
          </div>
        </div>
      </div>

      {/* Mobile: Compact search-filter bar (replaces "Show Filters" toggle) */}
      <div className="lg:hidden" data-id="element-14217" data-mp="2xnjjidi0l44b">
        <MobileFilterBar selectedTags={selectedTags} onToggle={handleToggleTag} accentBg={accentBg} accentBtnText={accentBtnText} accentBorder={accentBorder} accentHoverBorder={accentHoverBorder} accentHoverText={accentHoverText} accentText={accentText} data-id="element-14218" data-mp="2xol356d3iyxn" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex flex-col lg:flex-row gap-6 sm:gap-8" data-id="element-14219" data-mp="39pt66khbxmjl">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0" data-id="element-14220" data-mp="3ehb7xar6fr5m">
          <div className={`sticky top-[120px] rounded-xl border ${dk.card} p-4 max-h-[calc(100vh-140px)] overflow-y-auto`} data-id="element-14221" data-mp="3jfw5t3iyhyqw">
            <div className={`flex items-center gap-2 mb-2 pb-4 border-b ${dk.border}`} data-id="element-14222" data-mp="3nfmv12su46tb">
              <FilterIcon className={`h-5 w-5 ${accentText}`} data-id="element-14223" data-mp="3se7v92836qhr" />
              <h3 className={`font-bold ${dk.textHeading}`} data-id="element-14224" data-mp="18y9xnv1f0eh">Refine Stack</h3>
            </div>
            {filterContent}
          </div>
        </aside>

        {/* Results & Trending */}
        <main className="flex-1 min-w-0" data-id="element-14225" data-mp="230ky7hnjoh79">
          {selectedTags.length === 0 && <div className="mb-8 sm:mb-12" data-id="element-14226" data-mp="1vidb6p1i8rf8">
              <h3 className={`text-lg sm:text-xl font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-14227" data-mp="1vj68kzekq4kp">
                <FlameIcon className="h-5 w-5 mr-2 text-orange-500" data-id="element-14228" data-mp="1ls1vk58gktsi" />
                {isAfterTide ? 'Trending Dark Stacks' : 'Trending Story Stacks'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-14229" data-mp="1gus6wrsw8m5q">
                {TRENDING_STACKS.map((stack, idx) => <button key={idx} onClick={() => setSelectedTags(stack.tags)} className={`text-left p-4 sm:p-5 rounded-xl border transition-all hover:shadow-md ${accentHoverBorder} ${dk.card}`} data-id="element-14230" data-mp="29hw6gh97w8a4" data-mpi={idx}>
                    <h4 className={`font-bold text-sm sm:text-base mb-2 sm:mb-3 ${dk.textPrimary}`} data-id="element-14231" data-mp="1zlt3j96jk5m4">
                      {stack.name}
                    </h4>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-14232" data-mp="o6mjfxaggj27">
                      {stack.tags.map(tag => <span key={tag} className={`text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md ${dk.surfaceMuted} ${dk.textSecondary}`} data-id="element-14233" data-mp="2hppgvs1jf79k">
                          {tag}
                        </span>)}
                    </div>
                  </button>)}
              </div>
            </div>}

          <div className="flex items-center justify-between mb-4 sm:mb-6" data-id="element-14234" data-mp="30njlzuzjoymo">
            <h3 className={`text-base sm:text-xl font-bold ${dk.textHeading}`} data-id="element-14235" data-mp="2lrkk52313upz">
              {filteredBooks.length}{' '}
              {filteredBooks.length === 1 ? 'book matches' : 'books match'} your
              Story Stack
            </h3>
          </div>

          {filteredBooks.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6" data-id="element-14236" data-mp="1fbrx9yvcq2zg">
              {filteredBooks.map(book => <UnifiedBookCard key={book.id} book={book as any} darkMode={dk.isDark} data-id="element-14237" data-mp="3tkaza2owtlh7" />)}
            </div> : <div className={`text-center py-12 sm:py-16 rounded-xl border border-dashed ${dk.card}`} data-id="element-14238" data-mp="2c6vv3gbkmno">
              <LayersIcon className={`h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-14239" data-mp="3ar742cw1n0kh" />
              <h3 className={`text-base sm:text-lg font-bold mb-2 ${dk.textPrimary}`} data-id="element-14240" data-mp="35ugodqo5i6zl">
                No exact matches found
              </h3>
              <p className={`max-w-md mx-auto mb-6 text-sm sm:text-base ${dk.textSecondary}`} data-id="element-14241" data-mp="3etbxbaikxv6l">
                Try removing some tags from your Story Stack to broaden your
                search.
              </p>
              <button onClick={clearAll} className={`px-5 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium ${accentBg} ${accentBtnText} ${accentBgHover} transition-colors text-sm sm:text-base`} data-id="element-14242" data-mp="2n0bdtk78kdfc">
                Clear Stack
              </button>
            </div>}
        </main>
      </div>
    </div>;
};
export default StoryStackDiscovery;